import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { List, type RowComponentProps, useListRef } from 'react-window';
import { useInfiniteLoader } from 'react-window-infinite-loader';

import { Checkbox } from 'antd';
import { STEP_SELECTOR_EMPTY_OPTIONS_MESSAGE } from 'constants/uiConstants';
import { remToPx } from 'utils/cssUnits';
import MergeClassNames from 'utils/MergeClassNames';

import OptionMetaRow from './OptionMetaRow';
import {
  getStepOptionId,
  PICKER_OPTION_ID_ATTRIBUTE,
  StepSelectorOption,
} from './stepConfig';

// react-window requires a raw pixel number for rowHeight, so rem values are
// converted at render time using the document root font size. This means the
// list rows scale correctly when the user changes their browser font size.
const ROW_HEIGHT_REM = 3.5; // 56px at 16px base
const ROW_HEIGHT_COMPACT_REM = 1.75; // 28px at 16px base

// A page that keeps erroring (e.g. a slow/hung GetByOrgActive call) never
// changes hasNextPage or items.length, so the retry loop below can't tell
// "still working through it" from "stuck forever" any other way than a cap.
const MAX_CONSECUTIVE_LOAD_FAILURES = 3;

interface CatalogueRowProps {
  items: StepSelectorOption[];
  selectedGlobalId: string | null;
  onSelectItem: (item: StepSelectorOption, event: React.MouseEvent) => void;
  isFetchingNextPage: boolean;
  hasLoadMoreFailed: boolean;
  isMultiSelectMode: boolean;
  checkedGlobalIds: Set<string>;
  compact?: boolean;
}

const CatalogueRow = ({
  ariaAttributes,
  index,
  style,
  items,
  selectedGlobalId,
  onSelectItem,
  isFetchingNextPage,
  hasLoadMoreFailed,
  isMultiSelectMode,
  checkedGlobalIds,
  compact = false,
}: RowComponentProps<CatalogueRowProps>) => {
  const item = items[index];

  if (!item) {
    return (
      <div
        style={style}
        {...ariaAttributes}
        className="flex items-center justify-center text-xs text-gray-muted"
      >
        {hasLoadMoreFailed
          ? 'Could not load more items'
          : isFetchingNextPage && 'Loading more...'}
      </div>
    );
  }

  const isChecked = !!item.globalId && checkedGlobalIds.has(item.globalId);
  const isHighlighted = selectedGlobalId === item.globalId;
  // Checked (fill) and highlighted (ring) are independent in multi-select
  // mode — a row can be either, both, or neither, and arrow-key navigation
  // must stay visible even over an already-checked row. In single-select
  // mode there's no checkbox, so the highlighted row keeps the original
  // fill-only look.
  const isFilled = isMultiSelectMode ? isChecked : isHighlighted;
  const showHighlightRing = isMultiSelectMode && isHighlighted;

  return (
    <button
      type="button"
      style={style}
      {...ariaAttributes}
      {...{ [PICKER_OPTION_ID_ATTRIBUTE]: getStepOptionId(item) }}
      className={MergeClassNames(
        'flex w-full gap-2 border-b border-gray-bright px-3 text-left transition-colors hover:bg-gray-smoke',
        compact ? 'items-center py-1' : 'items-start py-2',
        isFilled ? 'bg-success-light text-charcoal' : 'bg-white text-charcoal',
        showHighlightRing && 'ring-2 ring-inset ring-charcoal'
      )}
      onClick={(event) => onSelectItem(item, event)}
    >
      {isMultiSelectMode && (
        <Checkbox checked={isChecked} className={compact ? '' : 'mt-1'} />
      )}
      <div className="flex-1">
        <div
          className={MergeClassNames(
            compact
              ? 'text-sm leading-5 text-charcoal'
              : 'text-base leading-5 text-charcoal',
            (isFilled || isHighlighted) && 'font-semibold'
          )}
        >
          {item.label}
        </div>
        {!compact && <OptionMetaRow option={item} />}
      </div>
    </button>
  );
};

interface CatalogueOptionListProps {
  items: StepSelectorOption[];
  selectedGlobalId: string | null;
  onSelectItem: (item: StepSelectorOption, event: React.MouseEvent) => void;
  hasNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
  isFetchingNextPage: boolean;
  hasFetchNextPageError: boolean;
  isMultiSelectMode: boolean;
  checkedGlobalIds: Set<string>;
  compact?: boolean;
}

const CatalogueOptionList: React.FC<CatalogueOptionListProps> = ({
  items,
  selectedGlobalId,
  onSelectItem,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  hasFetchNextPageError,
  isMultiSelectMode,
  checkedGlobalIds,
  compact = false,
}) => {
  const listRef = useListRef(null);

  useEffect(() => {
    if (!selectedGlobalId) return;
    const index = items.findIndex((item) => item.globalId === selectedGlobalId);
    if (index < 0) return;
    listRef.current?.scrollToRow({ index, align: 'auto' });
  }, [selectedGlobalId, items, listRef]);

  const rowCount = hasNextPage ? items.length + 1 : items.length;

  const isRowLoaded = useCallback(
    (index: number) => index < items.length,
    [items.length]
  );

  // react-window-infinite-loader marks a requested row index as loaded
  // permanently, even if the page it fetched added nothing at that index.
  // A page can add zero rows to a tab's own list (e.g. a page of G items
  // already present via M overrides, or held back by the visibleItems gate).
  // Keep paging until this tab's list actually grows, or the source is
  // exhausted — otherwise it gets stuck after one page that added nothing.
  const itemsLengthRef = useRef(items.length);
  itemsLengthRef.current = items.length;
  const hasNextPageRef = useRef(hasNextPage);
  hasNextPageRef.current = hasNextPage;
  const hasFetchNextPageErrorRef = useRef(hasFetchNextPageError);
  hasFetchNextPageErrorRef.current = hasFetchNextPageError;

  const [hasLoadMoreFailed, setHasLoadMoreFailed] = useState(false);

  // react-window-infinite-loader can invoke loadMoreRows again for the same
  // page before the previous call's fetchNextPage() resolves, firing a
  // duplicate request for that page. Dedup only the individual fetchNextPage
  // call (not the whole retry loop below) — concurrent callers share the one
  // network request already in flight, then each independently re-checks its
  // own retry condition once it settles. Locking the whole loop instead would
  // let a legitimate later call (for a row revealed once this tab's list
  // grows) get silently swallowed into an earlier chain that's already
  // finishing, permanently starving that row since react-window-infinite-loader
  // never asks for it again.
  const fetchInFlightRef = useRef<Promise<unknown> | null>(null);

  const dedupedFetchNextPage = useCallback(() => {
    if (!fetchInFlightRef.current) {
      fetchInFlightRef.current = Promise.resolve(fetchNextPage()).finally(
        () => {
          fetchInFlightRef.current = null;
        }
      );
    }
    return fetchInFlightRef.current;
  }, [fetchNextPage]);

  // The parent remounts this component (key={activeCategoryKey}) on every
  // tab switch — it does not cancel an in-progress retry loop first. Without
  // this guard, switching away mid-retry (e.g. off a tab that needs several
  // pages before its list grows) leaves
  // the old instance's loop running in the background with nothing to stop
  // it short of the whole underlying collection being exhausted. Doing this
  // across several tabs piles up multiple unbounded fetch loops, each firing
  // cache updates that re-render the still-mounted parent — that cascade is
  // what freezes the page. isMountedRef bounds each abandoned instance to at
  // most the one request already in flight when it unmounted.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadMoreRows = useCallback(async () => {
    const lengthBeforeFetch = itemsLengthRef.current;
    let consecutiveFailures = 0;
    while (
      isMountedRef.current &&
      hasNextPageRef.current &&
      itemsLengthRef.current <= lengthBeforeFetch &&
      consecutiveFailures < MAX_CONSECUTIVE_LOAD_FAILURES
    ) {
      await dedupedFetchNextPage();
      // React Query defers the cache notification that refreshes our props
      // (itemsLengthRef, hasNextPageRef, hasFetchNextPageErrorRef) to a
      // macrotask (notifyManager's default `setTimeout(cb, 0)` scheduler),
      // while this loop resumes on a microtask. Without yielding a macrotask
      // here, every check below still sees pre-fetch values, so the loop
      // always fires one page more than it needs.
      await new Promise((resolve) => setTimeout(resolve, 0));
      consecutiveFailures = hasFetchNextPageErrorRef.current
        ? consecutiveFailures + 1
        : 0;
    }
    if (
      isMountedRef.current &&
      consecutiveFailures >= MAX_CONSECUTIVE_LOAD_FAILURES
    ) {
      setHasLoadMoreFailed(true);
    }
  }, [dedupedFetchNextPage]);

  const onRowsRendered = useInfiniteLoader({
    isRowLoaded,
    loadMoreRows,
    rowCount,
  });

  const rowProps = useMemo(
    () => ({
      items,
      selectedGlobalId,
      onSelectItem,
      isFetchingNextPage,
      hasLoadMoreFailed,
      isMultiSelectMode,
      checkedGlobalIds,
      compact,
    }),
    [
      items,
      selectedGlobalId,
      onSelectItem,
      isFetchingNextPage,
      hasLoadMoreFailed,
      isMultiSelectMode,
      checkedGlobalIds,
      compact,
    ]
  );

  if (items.length === 0) {
    return (
      <div className="p-4 text-sm text-gray-muted">
        {STEP_SELECTOR_EMPTY_OPTIONS_MESSAGE}
      </div>
    );
  }

  return (
    <List
      className="h-full min-w-[22rem] !overflow-x-hidden"
      listRef={listRef}
      rowComponent={CatalogueRow}
      rowCount={rowCount}
      rowHeight={remToPx(compact ? ROW_HEIGHT_COMPACT_REM : ROW_HEIGHT_REM)}
      rowProps={rowProps}
      onRowsRendered={onRowsRendered}
    />
  );
};

export default CatalogueOptionList;
